export type SerialSaveOutcome = 'performed' | 'skipped'

export type SerialSaveRequest<T> = {
  sequence: number
  payload: T
  promise: Promise<SerialSaveOutcome>
  isLatest: () => boolean
}

/**
 * Serialises draft writes so an older request can never land after a newer one.
 * Eligibility is checked when a queued write is about to start, so a debounce
 * callback captured from an obsolete form revision cannot execute merely
 * because it was enqueued after a newer explicit save.
 */
export function createSerialSaveQueue<T>(
  perform: (payload: T) => Promise<void>,
  isEligible: (payload: T) => boolean = () => true,
) {
  let tail: Promise<unknown> = Promise.resolve()
  let latestSequence = 0

  return {
    enqueue(payload: T): SerialSaveRequest<T> {
      const sequence = ++latestSequence
      const promise = tail
        .catch(() => undefined)
        .then(async (): Promise<SerialSaveOutcome> => {
          if (!isEligible(payload)) return 'skipped'
          await perform(payload)
          return 'performed'
        })
      tail = promise.catch(() => undefined)

      return {
        sequence,
        payload,
        promise,
        isLatest: () => sequence === latestSequence,
      }
    },
  }
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message
    return typeof message === 'string' ? message : ''
  }
  return typeof error === 'string' ? error : ''
}

const usageValidationMessages = [
  'Usage scope must be selected business areas, organisation-wide or unknown',
  'Usage business areas must be an array',
  'Choose at least one usage business area, organisation-wide or not sure',
  'Usage business area is not an active area in this organisation',
]

/**
 * Only recognised usage-scope validation failures are revision-sensitive.
 * Transport/provider failures must remain visible until an explicit retry.
 */
export function isUsageScopeValidationError(error: unknown): boolean {
  const message = errorMessage(error)
  return usageValidationMessages.some((candidate) => message.includes(candidate))
}
